/**
 * The panel a receptionist actually fills the document in.
 *
 * It replaces a flat scrolling list that had three problems, all of which came
 * from the list having no relationship to the piece of paper beside it:
 *
 * **It was in the wrong order.** The fields arrived alphabetical - Address,
 * Amex, Arrival Date, Cash, City - because that is the order `config.ini`
 * listed them in. Nobody reads a registration card alphabetically. Now every
 * field knows the box it belongs to, so the deck is in the order the document
 * puts them - down the card, left to right across each printed line. It is one
 * even grid rather than a grid per printed line: a line of seven cells cannot
 * be seven columns in a panel this narrow, and giving each line its own grid
 * only moved the ragged edge into the deck as holes and uneven gaps.
 *
 * **Tick boxes were text.** "Mastercard" and "Miss" were fields the scanner
 * could never read, listed among the fields it had failed to read, with an
 * empty box to type a tick into. They are checkboxes now, and they sit where
 * the card puts them - the title beside the name, the payment methods after
 * the contact details - rather than in a heap at the bottom.
 *
 * **Every field looked equally uncertain.** A green bar and a number next to
 * all of them, including the ones nothing had ever read. What matters is not
 * the number, it is which of four states a field is in: read confidently, read
 * doubtfully, corrected by a person, or never read at all. The number is still
 * there on hover, for the one person in ten who wants it.
 *
 * The toolbar exists for the same reason: with a card of twenty fields and six
 * flagged, "find the next one that needs me" was scrolling and squinting.
 */

import { useCallback, useMemo, useState } from 'react'
import { AlertTriangle, ArrowDown, Check, ListFilter } from 'lucide-react'
import type { CaptureCheckboxGroup, Extraction } from '@/lib/types'
import { Button } from '@/components/ui/primitives'
import { FieldRow } from '@/features/workspace/FieldRow'
import { CheckboxGroupField } from '@/features/workspace/CheckboxGroups'

/**
 * Two boxes this close together, as a fraction of the page, are printed on the
 * same line of the form. The registration card's rows are 6% of the page apart
 * and its cells agree on their top edge to five decimal places, so this is not
 * a delicate number - it only has to be smaller than a row.
 */
const SAME_ROW = 0.012

type Block =
  | { kind: 'field'; key: string; page: number; y: number; x: number; field: Extraction }
  | {
      kind: 'group'
      key: string
      page: number
      y: number
      x: number
      group: CaptureCheckboxGroup
    }

export interface DeckHandlers {
  value: (extraction: Extraction) => string
  setValue: (fieldKey: string, value: string) => void
  setCheckbox: (groupKey: string, options: string[]) => void
  flush: () => Promise<void>
  failed: string[]
}

export function FieldDeck({
  fields,
  groups,
  edits,
  activeField,
  onFocusField,
}: {
  fields: Extraction[]
  groups: CaptureCheckboxGroup[]
  edits: DeckHandlers
  activeField: string | null
  onFocusField: (fieldKey: string) => void
}) {
  const [onlyAttention, setOnlyAttention] = useState(false)

  const needsAttention = useCallback(
    (field: Extraction) =>
      field.needs_review ||
      edits.failed.includes(field.field_key) ||
      (field.is_required && !edits.value(field).trim()),
    [edits],
  )

  // Document order, flat. The printed rows decide the *order* - see `_rows` -
  // but they no longer each get a grid of their own: a line of the card that
  // holds seven cells cannot be seven columns in a panel this wide, so it wrapped
  // to 2 + 2 + 2 + 1 and left the seventh card stranded beside a hole, with a rule
  // above it. Seven such lines made a deck of holes and uneven gaps that read as
  // a broken grid. One grid for the whole deck: every card the same width, every
  // gap the same, no holes.
  const blocks = useMemo(() => _rows(fields, groups).flat(), [fields, groups])
  const attention = useMemo(() => fields.filter(needsAttention), [fields, needsAttention])
  const filled = fields.filter((f) => edits.value(f).trim()).length

  const visible = useMemo(() => {
    if (!onlyAttention) return blocks
    return blocks.filter((block) => block.kind === 'group' || needsAttention(block.field))
  }, [blocks, onlyAttention, needsAttention])

  function jumpToNext() {
    // From the one after the active field, wrapping, so pressing it repeatedly
    // walks the list rather than sticking on the first problem.
    const order = attention.map((f) => f.field_key)
    if (order.length === 0) return
    const from = activeField ? order.indexOf(activeField) : -1
    const next = order[(from + 1) % order.length]
    onFocusField(next)
    document.getElementById(`field-${next}`)?.focus()
  }

  if (fields.length === 0 && groups.length === 0) {
    return (
      <p className="text-[13px] text-[var(--text-muted)]">
        This document has no fields to fill in — it is signed as it is.
      </p>
    )
  }

  return (
    <div className="flex min-h-0 flex-col gap-2">
      {/* `shrink-0`: the deck below it is what gives way when the panel is
          short, not the count of what still needs checking. */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-[var(--border)] pb-2">
        <p className="text-[12px] text-[var(--text-muted)]">
          <span className="tabular font-medium text-[var(--text)]">
            {filled}/{fields.length}
          </span>{' '}
          filled
        </p>

        {attention.length > 0 ? (
          <>
            <span
              className="inline-flex items-center gap-1 text-[12px] font-medium text-[var(--warning)]"
              role="status"
            >
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              {attention.length} to check
            </span>
            <Button
              variant="ghost"
              size="sm"
              icon={<ArrowDown className="h-3.5 w-3.5" />}
              onClick={jumpToNext}
            >
              Next
            </Button>
          </>
        ) : (
          <span className="inline-flex items-center gap-1 text-[12px] text-[var(--success)]">
            <Check className="h-3.5 w-3.5" aria-hidden />
            Nothing flagged
          </span>
        )}

        <Button
          variant={onlyAttention ? 'subtle' : 'ghost'}
          size="sm"
          className="ms-auto"
          icon={<ListFilter className="h-3.5 w-3.5" />}
          aria-pressed={onlyAttention}
          disabled={attention.length === 0 && !onlyAttention}
          onClick={() => setOnlyAttention((on) => !on)}
        >
          {onlyAttention ? 'Showing flagged' : 'Only flagged'}
        </Button>
      </div>

      <div className="scroll-thin min-h-0 overflow-y-auto pe-1">
        <div
          // Sized against *this panel*, which is half a split pane, rather than
          // a viewport breakpoint - at 1280px the viewport is xl and the panel
          // is 600px, so asking for three columns at xl gave three 190px columns
          // with "Company/Travel Agent" truncated to "C…".
          //
          // `auto-fill`, not `auto-fit`: auto-fit collapses the tracks it has no
          // card for, so a deck ending on an odd card stretched that one card
          // across the whole panel while every other stayed one column wide.
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(13rem, 1fr))' }}
          // `items-start`, so a card is as tall as what is in it. Stretching
          // them to the tallest in the line meant one flagged field - which
          // carries a crop of the scan under its box - inflated the plain field
          // beside it into a box with 38px of nothing under its input, which
          // reads as a field that has lost something. Every ordinary card is
          // now exactly as tall as every other.
          className="grid items-start gap-2"
        >
          {visible.map((block) =>
            block.kind === 'field' ? (
              <FieldRow
                key={block.key}
                extraction={block.field}
                value={edits.value(block.field)}
                active={activeField === block.field.field_key}
                failed={edits.failed.includes(block.field.field_key)}
                onFocus={() => onFocusField(block.field.field_key)}
                onBlur={() => void edits.flush()}
                onChange={(next) => edits.setValue(block.field.field_key, next)}
              />
            ) : (
              <CheckboxGroupField
                key={block.key}
                group={block.group}
                onChange={edits.setCheckbox}
              />
            ),
          )}
        </div>

        {visible.length === 0 && (
          <p className="py-6 text-center text-[13px] text-[var(--text-muted)]">
            Nothing needs checking.
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Fields and tick-box groups in document order, gathered into printed rows.
 *
 * A field with no box on the page - one the template defines but the document
 * does not draw - sorts after everything on its page rather than being dropped
 * or floated to the top. It still has to be fillable; it just has nowhere to
 * point at.
 */
function _rows(fields: Extraction[], groups: CaptureCheckboxGroup[]): Block[][] {
  const blocks: Block[] = [
    ...fields.map((field, index): Block => {
      const at = field.overlay
      return {
        kind: 'field',
        key: field.field_key,
        page: field.page,
        y: at ? at.y : 1 + index / 1000,
        x: at ? at.x : 0,
        field,
      }
    }),
    ...groups.flatMap((group): Block[] => {
      const first = group.options.find((option) => option.overlay)
      if (!first?.overlay) return []
      return [
        {
          kind: 'group',
          key: group.group_key,
          page: first.page,
          y: first.overlay.y,
          x: first.overlay.x,
          group,
        },
      ]
    }),
  ]

  // A group with no boxes on the page still has to be answerable, so it goes
  // at the end of the deck rather than nowhere.
  const placed = new Set(blocks.filter((b) => b.kind === 'group').map((b) => b.key))
  for (const group of groups) {
    if (placed.has(group.group_key)) continue
    blocks.push({ kind: 'group', key: group.group_key, page: 99, y: 99, x: 0, group })
  }

  blocks.sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x)

  const rows: Block[][] = []
  for (const block of blocks) {
    const row = rows[rows.length - 1]
    const head = row?.[0]
    if (head && head.page === block.page && Math.abs(head.y - block.y) <= SAME_ROW) {
      row.push(block)
    } else {
      rows.push([block])
    }
  }
  return rows
}
